import type { ProductCostHistoryResponse } from "@nova-ai/shared"

import { useProductCostCoverage } from "../../services/use-product-cost-history"
import { QueryState } from "../layout/query-state"
import { costTimestamp } from "./product-cost-observations"

export function ProductCostStartingData() {
  const coverage = useProductCostCoverage()
  return (
    <QueryState isLoading={coverage.isLoading} error={coverage.error}>
      {coverage.data ? (
        <p className="text-sm text-muted-foreground">
          {`Starting data: ${coverage.data.recordedIdentities} of ${coverage.data.identities} identities recorded; ${coverage.data.unresolvedIdentities} unresolved.`}

          {coverage.data.baseline.complete
            ? " Starting data complete."
            : " Starting data incomplete."}

          {coverage.data.captureActive ? " Capture active." : " Capture inactive."}
        </p>
      ) : null}
    </QueryState>
  )
}

export function ProductCostCoverage({
  coverage,
}: {
  coverage: ProductCostHistoryResponse["coverage"]
}) {
  return (
    <section
      aria-label="Cost observation coverage"
      className="space-y-3 rounded-xl bg-card p-5 text-sm"
    >
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <span>
          {coverage.baselineRecorded ? "Starting value recorded" : "Starting value not recorded"}
        </span>

        <span>
          {coverage.catalogPresent
            ? "Present in current catalog"
            : "Historical identity · absent from current catalog"}
        </span>

        <span>{coverage.captureActive ? "Capture active" : "Capture inactive"}</span>
      </div>

      <p className="text-muted-foreground">
        {`Last successful identity check: ${costTimestamp(coverage.lastSuccessfulCheckAt)}`}
      </p>

      <p className="text-muted-foreground">
        {`Last successful store check: ${costTimestamp(coverage.lastSuccessfulSourceCheckAt)}`}
      </p>

      <p className="text-muted-foreground">
        {coverage.lastSourceCheck
          ? `Latest store check: ${coverage.lastSourceCheck.ok ? "Succeeded" : "Failed"} · ${coverage.lastSourceCheck.mode} · ${costTimestamp(coverage.lastSourceCheck.finishedAt)}`
          : "Latest store check: Not recorded"}
      </p>

      <p className="max-w-prose text-muted-foreground">{coverage.limitation}</p>

      <details>
        <summary className="cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {`Capture periods and interruptions (${coverage.intervals.length} periods, ${coverage.gaps.length} gaps)`}
        </summary>

        <div className="mt-3 space-y-3">
          <p>{`Source version: ${costTimestamp(coverage.sourceVersionAt)}`}</p>

          {coverage.intervals.map((interval, index) => (
            <p key={`interval-${index}`}>
              {`Capture: ${costTimestamp(interval.startedAt)} — ${interval.endedAt ? costTimestamp(interval.endedAt) : "Ongoing"}. ${interval.reason}`}
            </p>
          ))}

          {coverage.gaps.map((gap, index) => (
            <p key={`gap-${index}`}>
              {`Interruption: ${costTimestamp(gap.startedAt)} — ${gap.endedAt ? costTimestamp(gap.endedAt) : "Ongoing"}. ${gap.reason}`}
            </p>
          ))}

          {coverage.intervals.length === 0 ? (
            <p>No active coverage period has been recorded.</p>
          ) : null}
        </div>
      </details>

      {coverage.coverageTruncated ? (
        <p>Older coverage periods are omitted from this bounded response.</p>
      ) : null}
    </section>
  )
}
